import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import pageBackgroundService from '../../services/pageBackgroundService';
import { BackgroundCard } from '../../components/backgrounds';
import BackgroundEditModal from './BackgroundEditModal';
import './PageBackgroundsPage.css';

function PageBackgroundsPage() {
  // State
  const [backgrounds, setBackgrounds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch backgrounds on mount
  useEffect(() => {
    fetchBackgrounds();
  }, []);

  const fetchBackgrounds = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await pageBackgroundService.getAll();
      // Sort backgrounds: home page first, then product-listing
      const sortedBackgrounds = (response.data || []).sort((a, b) => {
        if (a.page === 'home') return -1;
        if (b.page === 'home') return 1;
        return 0;
      });
      setBackgrounds(sortedBackgrounds);
    } catch (err) {
      console.error('Failed to fetch backgrounds:', err);
      setError('Failed to load page backgrounds. Please try again.');
      toast.error('Failed to load backgrounds');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = useCallback((background) => {
    setSelectedBackground(background);
    setIsModalOpen(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBackground(null);
  }, []);

  // Handle save
  const handleSave = useCallback(async (id, data) => {
    setIsSaving(true);
    try {
      const response = await pageBackgroundService.update(id, data);
      setBackgrounds((prev) =>
        prev.map((bg) => (bg.id === id ? response.data : bg))
      );
      toast.success('Background updated successfully');
      handleCloseModal();
    } catch (err) {
      console.error('Failed to update background:', err);
      toast.error('Failed to update background');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [handleCloseModal]);

  // Loading state
  if (isLoading) {
    return (
      <div className="page-backgrounds-page">
        <div className="page-header">
          <div>
            <div className="skeleton skeleton-title" />
            <div className="skeleton skeleton-subtitle" />
          </div>
        </div>
        <div className="backgrounds-grid">
          {[1, 2].map((i) => (
            <div key={i} className="skeleton-background-card" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-backgrounds-page">
        <div className="error-state">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            className="error-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <h3>Error Loading Backgrounds</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchBackgrounds}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-backgrounds-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Page Backgrounds</h1>
          <p>Manage background images for website pages</p>
        </div>
        <a
          href={process.env.REACT_APP_ECOMMERCE_URL || 'http://localhost:3000'}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="btn-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
            />
          </svg>
          View Live Site
        </a>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="info-icon"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
        <p>
          These backgrounds are displayed on the <strong>Home</strong> and{' '}
          <strong>Product Listing</strong> pages. Changes sync automatically to the live site.
        </p>
      </div>

      {/* Backgrounds Grid */}
      <div className="backgrounds-grid">
        {backgrounds.map((bg) => (
          <BackgroundCard
            key={bg.id}
            background={bg}
            onEdit={handleEdit}
            isUpdating={isSaving && selectedBackground?.id === bg.id}
          />
        ))}
      </div>

      {/* Empty State */}
      {backgrounds.length === 0 && (
        <div className="empty-state">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            className="empty-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
            />
          </svg>
          <h3>No backgrounds found</h3>
          <p>Page backgrounds have not been configured yet.</p>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && selectedBackground && (
        <BackgroundEditModal
          background={selectedBackground}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}

export default PageBackgroundsPage;
